import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Table, Tag, Space, Button, Select, Popconfirm, Modal, Descriptions, Typography } from "antd";
import moment from "moment";

import {
  fetchAllCustomPackageRequests,
  updateCustomPackageRequestStatus,
  deleteCustomPackageRequest,
} from "../../features/packages/packagesThunk";

const { Text } = Typography;

const STATUS_OPTIONS = ["REQUESTED", "APPROVED", "DENIED"];

const STATUS_COLORS = {
  REQUESTED: "gold",
  APPROVED: "green",
  DENIED: "red",
};

const money = (amount) => `NGN ${Number(amount || 0).toLocaleString()}`;

const CustomPackageRequestsPanel = () => {
  const dispatch = useDispatch();
  const { customPackageRequests, customPackageRequestsLoading, customPackageRequestActionLoading } =
    useSelector((state) => state.packages);

  const [viewing, setViewing] = useState(null);

  const load = (page = 0, pageSize = 10) =>
    dispatch(fetchAllCustomPackageRequests({ page, pageSize }));

  useEffect(() => {
    load(0, 10);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleStatusChange = (id, status) =>
    dispatch(updateCustomPackageRequestStatus({ id, status }));

  const handleDelete = async (id) => {
    const result = await dispatch(deleteCustomPackageRequest(id));
    if (!result.error) {
      const { page, pageSize } = customPackageRequests;
      load(page, pageSize);
    }
  };

  const columns = [
    {
      title: "Destination",
      dataIndex: "destination",
      key: "destination",
    },
    {
      title: "Requester",
      dataIndex: "user_email",
      key: "user_email",
    },
    {
      title: "Dates",
      key: "dates",
      render: (_, record) =>
        `${record.start_date ? moment(record.start_date).format("YYYY-MM-DD") : "—"} → ${
          record.end_date ? moment(record.end_date).format("YYYY-MM-DD") : "—"
        }`,
    },
    {
      title: "Travellers",
      dataIndex: "number_of_travelers",
      key: "number_of_travelers",
    },
    {
      title: "Budget",
      key: "budget",
      render: (_, record) => money(record.budget),
    },
    {
      title: "Status",
      key: "status",
      render: (_, record) => (
        <Select
          size="small"
          value={record.status}
          style={{ width: 130 }}
          loading={customPackageRequestActionLoading}
          onChange={(status) => handleStatusChange(record.id, status)}
          options={STATUS_OPTIONS.map((s) => ({
            value: s,
            label: <Tag color={STATUS_COLORS[s]}>{s}</Tag>,
          }))}
        />
      ),
    },
    {
      title: "Submitted",
      key: "created_at",
      render: (_, record) =>
        record.created_at ? moment(record.created_at).format("YYYY-MM-DD") : "—",
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <Space>
          <Button size="small" onClick={() => setViewing(record)}>
            View
          </Button>
          <Popconfirm
            title="Delete this request?"
            description="Permanent — there's no restore for custom package requests."
            okText="Delete"
            okButtonProps={{ danger: true }}
            onConfirm={() => handleDelete(record.id)}
          >
            <Button size="small" danger>
              Delete
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Table
        rowKey="id"
        loading={customPackageRequestsLoading}
        columns={columns}
        dataSource={customPackageRequests?.data || []}
        pagination={{
          current: (customPackageRequests?.page ?? 0) + 1,
          pageSize: customPackageRequests?.pageSize || 10,
          total: customPackageRequests?.total || 0,
          onChange: (page, pageSize) => load(page - 1, pageSize),
        }}
        style={{ marginTop: 16 }}
      />

      <Modal
        title="Custom Package Request"
        open={!!viewing}
        onCancel={() => setViewing(null)}
        footer={
          <Button type="primary" onClick={() => setViewing(null)}>
            Close
          </Button>
        }
      >
        {viewing && (
          <Descriptions column={1} bordered size="small">
            <Descriptions.Item label="Destination">{viewing.destination}</Descriptions.Item>
            <Descriptions.Item label="Requester">{viewing.user_email}</Descriptions.Item>
            <Descriptions.Item label="Dates">
              {viewing.start_date ? moment(viewing.start_date).format("YYYY-MM-DD") : "—"} →{" "}
              {viewing.end_date ? moment(viewing.end_date).format("YYYY-MM-DD") : "—"}
            </Descriptions.Item>
            <Descriptions.Item label="Travellers">{viewing.number_of_travelers}</Descriptions.Item>
            <Descriptions.Item label="Budget">{money(viewing.budget)}</Descriptions.Item>
            <Descriptions.Item label="Status">
              <Tag color={STATUS_COLORS[viewing.status]}>{viewing.status}</Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Additional info">
              {viewing.additional_info || <Text type="secondary">—</Text>}
            </Descriptions.Item>
            <Descriptions.Item label="Submitted">
              {viewing.created_at ? moment(viewing.created_at).format("YYYY-MM-DD HH:mm") : "—"}
            </Descriptions.Item>
          </Descriptions>
        )}
      </Modal>
    </div>
  );
};

export default CustomPackageRequestsPanel;
